import { __read } from "tslib";
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FontIcon } from '@fluentui/react';
import { MenuItemTypes } from '@office-start/common-types';
import { FileType } from '@office-start/document-types';
import EdgeworthIcon from '@office-start/edgeworth-icon';
import { IconNames } from '@office-start/fabric-icon-subset-utility';
import { format } from '@office-start/string-formatter-utility';
import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityFilterKey, ActivityFilterKeyToFilledIconMap, ActivityFilterKeyToIconMap, } from '../models/ActivityFilters';
import { DateFilterKey } from '../models/DateFilters';
import { ConsumerFileFiltersMap, FileFilterKey, FileFiltersMap, FileFilterToFilledIconMap, FileFilterToIconMap, FileTypeToFilterFunctionMap, } from '../models/FileFilters';
import { FilterType, } from '../models/Filters';
import styles from '../views/BaseEdgeworthControl.module.scss';
import { useActivityFilterMap } from './useActivityFilterMap';
import { useDateFilterMap } from './useDateFilterMap';
import { useFilterNameMap } from './useFilterNameMap';
import { usePillFiltersMap } from './usePillFiltersMap';
export var AccordionFilterKey;
(function (AccordionFilterKey) {
    AccordionFilterKey["FileType"] = "FileType";
    AccordionFilterKey["Activity"] = "Activity";
    AccordionFilterKey["Date"] = "Date";
})(AccordionFilterKey || (AccordionFilterKey = {}));
export function useAccordionFilters(_a) {
    var draftEnabled = _a.draftEnabled, locResources = _a.locResources, identity = _a.identity, linksEnabled = _a.linksEnabled, fileType = _a.fileType, fluidSupportEnabled = _a.fluidSupportEnabled, currentTab = _a.currentTab, onSelectFilter = _a.onSelectFilter, msaUserHint = _a.msaUserHint;
    var filterNameMap = useFilterNameMap(locResources);
    var fileFiltersMap = identity.isMsa
        ? ConsumerFileFiltersMap
        : FileFiltersMap;
    var activityFilterFunctionMap = useActivityFilterMap(identity, msaUserHint);
    var dateFilterFunctionMap = useDateFilterMap(currentTab === 'recent');
    var pillFilterMap = usePillFiltersMap(locResources);
    var _b = __read(useState(fileType && FileTypeToFilterFunctionMap.has(fileType)
        ? [
            {
                filterId: FileType[fileType].toString(),
                filterFunction: FileTypeToFilterFunctionMap.get(fileType),
                filterType: FilterType.FileType,
            },
        ]
        : []), 2), fileFilters = _b[0], setFileFilters = _b[1];
    var _c = __read(useState([]), 2), activitiesFilters = _c[0], setActivitiesFilters = _c[1];
    var _d = __read(useState(undefined), 2), dateFilter = _d[0], setDateFilter = _d[1];
    var _e = __read(useState([]), 2), activePillFilters = _e[0], setActivePillFilters = _e[1];
    var _f = __read(useState(false), 2), resetFilterKey = _f[0], setResetFilterKey = _f[1];
    var resetFilterFunctions = useCallback(function () {
        if (!fileType) {
            setFileFilters([]);
        }
        setActivitiesFilters([]);
        setDateFilter(undefined);
        setActivePillFilters([]);
        setResetFilterKey(!resetFilterKey);
    }, [fileType, resetFilterKey]);
    var numActiveFilters = useMemo(function () {
        return ((!!fileType ? 0 : fileFilters.length) +
            (!!dateFilter ? 1 : 0) +
            activitiesFilters.length);
    }, [activitiesFilters.length, dateFilter, fileFilters.length, fileType]);
    var filterHeaders = useMemo(function () {
        var fileTypeFilterHeader = fileFilters.length
            ? format(locResources.typesAppliedFilterText, fileFilters.length.toString())
            : locResources.typeFilterText;
        var activityFilterHeader = activitiesFilters.length
            ? format(locResources.activitiesFiltersAppliedText, activitiesFilters.length.toString())
            : locResources.activityText;
        var timeFilterHeader = dateFilter
            ? locResources.timeFilterAppliedLabel
            : locResources.timeFilterLabel;
        return {
            fileTypeFilterHeader: fileTypeFilterHeader,
            activityFilterHeader: activityFilterHeader,
            timeFilterHeader: timeFilterHeader,
        };
    }, [
        activitiesFilters.length,
        dateFilter,
        fileFilters.length,
        locResources,
    ]);
    var accordionItems = useMemo(function () {
        var innerFileTypeFilterItems = Object.values(FileFilterKey)
            .filter(
        //Disable Fluid filter option when fluidSupportEnabled is false
        //Disable links option when linksEnabled is false
        //Disable Meetings filter option when on the meeting tab
        function (filterKey) {
            return (filterKey !== FileFilterKey.fluid ||
                fluidSupportEnabled) &&
                (filterKey !== FileFilterKey.link || linksEnabled) &&
                (filterKey !== FileFilterKey.drafts || draftEnabled) &&
                currentTab !== 'meetings';
        })
            .map(function (filterKey) {
            var _a;
            if (!fileFiltersMap.has(filterKey)) {
                return undefined;
            }
            var filledIcon = FileFilterToFilledIconMap.get(filterKey);
            return {
                filterType: FilterType.FileType,
                key: filterKey,
                iconContent: (React.createElement("div", { className: classNames(styles['accordion-icon'], (_a = {},
                        _a[styles['fluid-icon']] = filterKey === 'Fluid Filter',
                        _a)) }, FileFilterToIconMap.get(filterKey))),
                selectedIconContent: !!filledIcon && (React.createElement("div", { className: styles['accordion-icon'] }, filledIcon)),
                filterFunction: fileFiltersMap.get(filterKey),
                stringContent: filterNameMap.get(filterKey),
                menuItemType: MenuItemTypes.Checkbox,
            };
        })
            .filter(function (filterItem) { return !!filterItem; });
        var innerActivityFilterItems = Object.values(ActivityFilterKey)
            .map(function (filterKey) {
            if (!activityFilterFunctionMap.has(filterKey)) {
                return undefined;
            }
            var filledIcon = ActivityFilterKeyToFilledIconMap.get(filterKey);
            return {
                filterType: FilterType.Activity,
                key: filterKey,
                iconContent: (React.createElement("div", { className: styles['accordion-icon'] }, ActivityFilterKeyToIconMap.get(filterKey))),
                selectedIconContent: !!filledIcon && (React.createElement("div", { className: styles['accordion-icon'] }, filledIcon)),
                filterFunction: activityFilterFunctionMap.get(filterKey),
                stringContent: filterNameMap.get(filterKey),
                menuItemType: MenuItemTypes.Checkbox,
            };
        })
            .filter(function (filterItem) { return !!filterItem; });
        var innerTimeFilterItems = Object.values(DateFilterKey).map(function (filterKey) {
            return {
                filterType: FilterType.Date,
                key: filterKey,
                stringContent: filterNameMap.get(filterKey),
                filterFunction: dateFilterFunctionMap.get(filterKey),
                menuItemType: MenuItemTypes.SingleCheck,
            };
        });
        var accordionItems = [
            {
                key: AccordionFilterKey.FileType,
                content: filterHeaders.fileTypeFilterHeader,
                menuItemType: MenuItemTypes.Checkbox,
                multiSelect: true,
                getInnerItems: function () {
                    return Promise.resolve(innerFileTypeFilterItems);
                },
                isHidden: !!fileType,
                defaultSelected: activePillFilters
                    .filter(function (pill) { return !!fileFiltersMap.get(pill.id || ''); })
                    .map(function (pill) { return pill.id; }),
            },
            {
                key: AccordionFilterKey.Activity,
                content: filterHeaders.activityFilterHeader,
                menuItemType: MenuItemTypes.Checkbox,
                multiSelect: true,
                getInnerItems: function () {
                    return Promise.resolve(innerActivityFilterItems);
                },
                defaultSelected: activePillFilters
                    .filter(function (pill) {
                    return !!activityFilterFunctionMap.get(pill.id || '');
                })
                    .map(function (pill) { return pill.id; }),
            },
            {
                key: AccordionFilterKey.Date,
                content: filterHeaders.timeFilterHeader,
                menuItemType: MenuItemTypes.SingleCheck,
                defaultSelected: DateFilterKey.anytime,
                getInnerItems: function () { return Promise.resolve(innerTimeFilterItems); },
            },
        ];
        if (numActiveFilters) {
            var numActiveFiltersString = format(locResources.numberOfFiltersAppliedHeader, numActiveFilters.toString());
            var content = (React.createElement(React.Fragment, null,
                React.createElement("div", { className: styles['filter-accordion-header'] }, numActiveFiltersString),
                React.createElement("button", { className: styles['clear-filters-button'], tabIndex: 0, onClick: resetFilterFunctions }, locResources.clearFilterButtonName)));
            var accordionHeaderItem = {
                key: 'AccordionFilterHeader',
                content: content,
                isHeader: true,
            };
            accordionItems.unshift(accordionHeaderItem);
        }
        return accordionItems;
    }, [
        activePillFilters,
        activityFilterFunctionMap,
        currentTab,
        dateFilterFunctionMap,
        draftEnabled,
        fileFiltersMap,
        fileType,
        filterHeaders.activityFilterHeader,
        filterHeaders.fileTypeFilterHeader,
        filterHeaders.timeFilterHeader,
        filterNameMap,
        fluidSupportEnabled,
        linksEnabled,
        locResources.clearFilterButtonName,
        locResources.numberOfFiltersAppliedHeader,
        numActiveFilters,
        resetFilterFunctions,
    ]);
    var renderItem = useCallback(function (item) {
        return React.createElement("div", null, item.content);
    }, []);
    var renderInnerItem = useCallback(function (item, isSelected) {
        var _a;
        return (React.createElement("div", { key: item.key, className: styles['accordion-item__wrapper'], "aria-label": item.stringContent },
            React.createElement("div", { className: isSelected
                    ? styles['accordion-item__icon--selected']
                    : '' }, isSelected && item.selectedIconContent
                ? item.selectedIconContent
                : (_a = item.iconContent) !== null && _a !== void 0 ? _a : null),
            React.createElement("div", { className: styles['accordion-item__text'] }, item.stringContent)));
    }, []);
    var addFilter = useCallback(function (filterId, filterType) {
        var filterPill = pillFilterMap === null || pillFilterMap === void 0 ? void 0 : pillFilterMap.get(filterId);
        var updatedActivePillFilters = activePillFilters;
        switch (filterType) {
            case FilterType.Date:
                updatedActivePillFilters = updatedActivePillFilters.filter(function (pill) { return pill && pill.filterType !== FilterType.Date; });
                var dateFilterFunction = dateFilterFunctionMap.get(filterId);
                var newDateFilterModel = dateFilterFunction
                    ? {
                        filterId: filterId,
                        filterFunction: dateFilterFunction,
                        filterType: FilterType.Date,
                    }
                    : undefined;
                setDateFilter(newDateFilterModel);
                break;
            case FilterType.FileType:
                var fileTypeFilterFunction = fileFiltersMap.get(filterId);
                if (fileTypeFilterFunction) {
                    var newFileFilterModel = {
                        filterId: filterId,
                        filterFunction: fileTypeFilterFunction,
                        filterType: FilterType.FileType,
                    };
                    setFileFilters(fileFilters.concat([newFileFilterModel]));
                }
                break;
            case FilterType.Activity:
                var activityFilterFunction = activityFilterFunctionMap.get(filterId);
                if (activityFilterFunction) {
                    var newActivityFilterModel = {
                        filterId: filterId,
                        filterFunction: activityFilterFunction,
                        filterType: FilterType.Activity,
                    };
                    setActivitiesFilters(activitiesFilters.concat([newActivityFilterModel]));
                }
                break;
        }
        if (filterPill !== undefined) {
            // we dont want to show "anytime" filter as pivot
            if (filterId != DateFilterKey.anytime) {
                updatedActivePillFilters = updatedActivePillFilters.concat([
                    filterPill,
                ]);
            }
            setActivePillFilters(updatedActivePillFilters);
        }
    }, [
        pillFilterMap,
        activePillFilters,
        dateFilterFunctionMap,
        fileFiltersMap,
        activityFilterFunctionMap,
        fileFilters,
        activitiesFilters,
    ]);
    var removeFilter = useCallback(function (filterId, filterType) {
        setActivePillFilters(activePillFilters.filter(function (pill) { return pill && pill.id !== filterId; }));
        switch (filterType) {
            case FilterType.Date:
                setDateFilter(undefined);
                break;
            case FilterType.FileType:
                setFileFilters(fileFilters.filter(function (filter) { return filter.filterId !== filterId; }));
                break;
            case FilterType.Activity:
                setActivitiesFilters(activitiesFilters.filter(function (filter) { return filter.filterId !== filterId; }));
                break;
        }
    }, [activePillFilters, activitiesFilters, fileFilters]);
    var addOrRemoveFilter = useCallback(function (filterId, filterType, added) {
        if (added) {
            addFilter(filterId, filterType);
        }
        else {
            removeFilter(filterId, filterType);
        }
        if (onSelectFilter) {
            onSelectFilter(filterId, added);
        }
    }, [onSelectFilter, addFilter, removeFilter]);
    var onFilterPillRemove = useCallback(function (filterPill) {
        return function (_event) {
            if (filterPill.id && filterPill.filterType !== undefined) {
                addOrRemoveFilter(filterPill.id, filterPill.filterType, false);
                setResetFilterKey(!resetFilterKey);
            }
        };
    }, [resetFilterKey, addOrRemoveFilter]);
    var innerItemOnClick = useCallback(function (item, isSelected) {
        addOrRemoveFilter(item.key, item.filterType, !!isSelected);
    }, [addOrRemoveFilter]);
    var filterButtonTitle = useMemo(function () {
        return numActiveFilters
            ? format(locResources.filtersAppliedButtonTitle, numActiveFilters.toString())
            : locResources.filterButtonTitle;
    }, [
        locResources.filterButtonTitle,
        locResources.filtersAppliedButtonTitle,
        numActiveFilters,
    ]);
    var accordionButtonContent = useMemo(function () {
        return (React.createElement("div", { className: styles['accordion-button'] },
            React.createElement("div", { className: styles['accordion-button__icon'] },
                React.createElement(EdgeworthIcon, { iconName: "FilterIcon", filled: !!numActiveFilters })),
            React.createElement("div", { className: styles['accordion-button__text'] }, filterButtonTitle),
            React.createElement("div", { className: styles['accordion-button__chevron'] },
                React.createElement(FontIcon, { iconName: IconNames.ChevronDown }))));
    }, [filterButtonTitle, numActiveFilters]);
    var filterButtonAriaLabel = useMemo(function () {
        var ariaLabel = filterButtonTitle;
        ariaLabel += numActiveFilters
            ? format(locResources.numberOfFiltersAppliedHeader, '')
            : '';
        return ariaLabel;
    }, [
        filterButtonTitle,
        locResources.numberOfFiltersAppliedHeader,
        numActiveFilters,
    ]);
    //Need to re-create the onFilterPillClick as filters are applied/removed
    useMemo(function () {
        activePillFilters.forEach(function (pill) {
            pill.onRemoveClick = onFilterPillRemove(pill);
        });
    }, [activePillFilters, onFilterPillRemove]);
    return useMemo(function () { return ({
        filesFilter: {
            activeFilters: fileFilters,
        },
        activitiesFilter: {
            activeFilters: activitiesFilters,
        },
        dateFilter: {
            activeFilters: !!dateFilter ? [dateFilter] : [],
        },
        accordionProps: {
            items: accordionItems,
            renderItem: renderItem,
            renderInnerItem: renderInnerItem,
            innerItemOnClick: innerItemOnClick,
            collapsed: false,
            createOption: false,
            key: resetFilterKey,
        },
        resetFilterFunctions: resetFilterFunctions,
        numAccordionFilters: numActiveFilters,
        accordionButtonContent: accordionButtonContent,
        filterTitle: filterButtonTitle,
        filterButtonAriaLabel: filterButtonAriaLabel,
        showHidden: !!activitiesFilters.find(function (activityFilter) {
            return activityFilter.filterId === ActivityFilterKey.hidden;
        }),
        activePillFilters: activePillFilters,
    }); }, [
        fileFilters,
        activitiesFilters,
        dateFilter,
        accordionItems,
        renderItem,
        renderInnerItem,
        innerItemOnClick,
        resetFilterKey,
        resetFilterFunctions,
        numActiveFilters,
        accordionButtonContent,
        filterButtonTitle,
        filterButtonAriaLabel,
        activePillFilters,
    ]);
}
//# sourceMappingURL=useAccordionFilters.js.map